import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { getRdv } from '../services/api';
import { useTranslation } from 'react-i18next';

// Configuration de moment (locale par défaut : français)
moment.locale('fr');
const localizer = momentLocalizer(moment);

// Couleurs par défaut pour les statuts
const statusColors = {
  "Confirme Paye": "#4caf50",
  "POSITIONNÉ A CONFIRMER": "#ff9800",
  "Annulé": "#f44336",
  "En Attente": "#2196f3"
};

const CalendarPage = () => {
  const { t } = useTranslation();

  // États pour les données et filtres
  const [rdvs, setRdvs] = useState([]);
  const [teamFilter, setTeamFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [origineFilter, setOrigineFilter] = useState('');
  const [filialeFilter, setFilialeFilter] = useState('');
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Récupération des RDV au montage
  useEffect(() => {
    const fetchRdvs = async () => {
      const data = await getRdv();
      setRdvs(data);
    };
    fetchRdvs();
  }, []);

  // Filtrage des rendez‑vous
  const filteredRdvs = rdvs.filter(rdv => {
    const matchTeam = teamFilter ? rdv.equipe === teamFilter : true;
    const matchStatus = statusFilter ? (rdv.status || '').toLowerCase() === statusFilter.toLowerCase() : true;
    const matchOrigine = origineFilter ? (rdv.Origine || '') === origineFilter : true;
    const matchFiliale = filialeFilter ? (rdv.filiale || '') === filialeFilter : true;
    return matchTeam && matchStatus && matchOrigine && matchFiliale;
  });

  // Transformation des RDV en événements pour le calendrier
  const events = filteredRdvs.map(rdv => {
    const title = `${rdv.Nom || ''} ${rdv.Prenom || ''} - ${rdv.cp || ''} - ${rdv.status || ''}`;
    return {
      title,
      start: new Date(`${rdv.rdv_date}T${rdv.rdv_time}`),
      end: new Date(new Date(`${rdv.rdv_date}T${rdv.rdv_time}`).getTime() + 60 * 60 * 1000),
      status: rdv.status,
      rdv: rdv
    };
  });

  // Gestion de la sélection d'un événement
  const handleSelectEvent = (event) => {
    setSelectedRdv(event.rdv);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Définir l'intervalle horaire pour l'affichage du calendrier (de 8h à 20h)
  const minTime = new Date();
  minTime.setHours(8, 0, 0);
  const maxTime = new Date();
  maxTime.setHours(20, 0, 0);

  // Extraction des options distinctes, triées alphabétiquement
  const distinctTeams = Array.from(new Set(rdvs.map(rdv => rdv.equipe).filter(Boolean)))
    .sort((a, b) => {
      // Extraire les numéros des équipes
      const numA = parseInt(a.replace('EQUIPE ', ''));
      const numB = parseInt(b.replace('EQUIPE ', ''));
      return numA - numB;
    });
  const distinctStatuses = Array.from(new Set(rdvs.map(rdv => rdv.status).filter(Boolean)));
  const distinctOrigines = Array.from(new Set(rdvs.map(rdv => rdv.Origine).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b));
  const distinctFiliales = Array.from(new Set(rdvs.map(rdv => rdv.filiale).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b));

  // Personnalisation du style des événements
  const eventStyleGetter = (event) => {
    const backgroundColor = event.rdv.color || statusColors[event.status] || "#3174ad";
    const style = {
      backgroundColor,
      borderRadius: '5px',
      opacity: 0.9,
      color: 'white',
      border: '0px',
      display: 'block'
    };
    return { style };
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('calendarPage.rdvCalendar')}
      </Typography>
      
      {/* Filtrage des RDV */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="team-filter-label">{t('filters.team')}</InputLabel>
          <Select
            labelId="team-filter-label"
            value={teamFilter}
            label={t('filters.team')}
            onChange={(e) => setTeamFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="">
              <em>{t('filters.all', { defaultValue: "Toutes" })}</em>
            </MenuItem>
            {distinctTeams.map(team => (
              <MenuItem key={team} value={team}>{team}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">{t('table.column.status')}</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label={t('table.column.status')}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="">
              <em>{t('filters.all', { defaultValue: "Tous" })}</em>
            </MenuItem>
            {distinctStatuses.map(status => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="origin-filter-label">{t('filters.origin')}</InputLabel>
          <Select
            labelId="origin-filter-label"
            value={origineFilter}
            label={t('filters.origin')}
            onChange={(e) => setOrigineFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="">
              <em>{t('filters.all', { defaultValue: "Toutes" })}</em>
            </MenuItem>
            {distinctOrigines.map(origine => (
              <MenuItem key={origine} value={origine}>{origine}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="branch-filter-label">{t('filters.branch')}</InputLabel>
          <Select
            labelId="branch-filter-label"
            value={filialeFilter}
            label={t('filters.branch')}
            onChange={(e) => setFilialeFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="">
              <em>{t('filters.all', { defaultValue: "Toutes" })}</em>
            </MenuItem>
            {distinctFiliales.map(filiale => (
              <MenuItem key={filiale} value={filiale}>{filiale}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Button variant="outlined" onClick={() => {
          setTeamFilter('');
          setStatusFilter('');
          setOrigineFilter('');
          setFilialeFilter('');
        }}>
          {t('filters.reset')}
        </Button>
      </Box>

      {/* Affichage du calendrier */}
      <Box sx={{ height: 700 }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          eventPropGetter={eventStyleGetter}
          views={['month', 'week', 'day', 'agenda']}
          defaultView="week"
          min={minTime}
          max={maxTime}
          onSelectEvent={handleSelectEvent}
          messages={{
            today: t('calendarPage.today'),
            previous: t('calendarPage.previous'),
            next: t('calendarPage.next'),
            month: t('calendarPage.month'),
            week: t('calendarPage.week'),
            day: t('calendarPage.day'),
            agenda: t('calendarPage.agenda')
          }}
        />
      </Box>

      {/* Popup de détails du RDV */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle 
          sx={{ 
            bgcolor: (selectedRdv?.color || statusColors[selectedRdv?.status]) || 'primary.main',
            color: 'white'
          }}
        >
          {t('rdvDetails.details')}
        </DialogTitle>
        <DialogContent dividers>
          {selectedRdv && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('rdvDetails.client')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('table.column.client')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedRdv.Nom} {selectedRdv.Prenom}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('rdvDetails.address')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedRdv.address}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('rdvDetails.postalCode')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedRdv.cp}
                    </Typography>
                  </Box>
                </Paper>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('rdvDetails.appointment')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('rdvDetails.dateTime')}
                    </Typography>
                    <Typography variant="body1">
                      {moment(`${selectedRdv.rdv_date}T${selectedRdv.rdv_time}`).format('LLLL')}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('table.column.team')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedRdv.equipe || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('table.column.status')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedRdv.status}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('rdvDetails.appointment')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('table.column.surface')} 
                    </Typography>
                    <Typography variant="body1">{selectedRdv.surface || "-"}</Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('table.column.estimatedSurface')}
                    </Typography>
                    <Typography variant="body1">{selectedRdv.surface_estime || "-"}</Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('table.column.installedSurface')}
                    </Typography>
                    <Typography variant="body1">{selectedRdv.surface_installe || "-"}</Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('table.column.origin')}
                    </Typography>
                    <Typography variant="body1">{selectedRdv.Origine || "-"}</Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('table.column.installationType')}
                    </Typography>
                    <Typography variant="body1">{selectedRdv.type_install || "-"}</Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('table.column.branch')}
                    </Typography>
                    <Typography variant="body1">{selectedRdv.filiale || "-"}</Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedRdv?.leadid && (
            <Button
              variant="contained"
              color="primary"
              href={`https://kortex.phone.gs/vtigercrm/index.php?module=Leads&view=Detail&record=${selectedRdv.leadid}&app=MARKETING`}
              target="_blank"
            >
              {t('rdvDetails.accessFile')}
            </Button>
          )}
          <Button onClick={handleCloseDialog} variant="contained">
            {t('rdvDetails.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarPage;
